import { ChainId } from '@monadex/sdk'
import { Box, CircularProgress } from '@mui/material'
import { Button } from '@mui/base'
import { CustomModal } from '@/components'
import { useWalletData } from '@/utils'
import Image from 'next/image'
import rejected from '@/static/assets/rejected.webp'
import { Cancel01Icon, CheckmarkCircle01Icon, ArrowDown02Icon } from '@/utils/icons'
interface useConfirmationPendingContentProps {
  title: string
  pending?: string
  confirm: string
}
export const useConfirmationPendingContent = (pendingText?: string): useConfirmationPendingContentProps => {
  return {
    title: 'Waiting for Confirmation',
    pending: pendingText,
    confirm: 'Please confirm this transaction in your wallet.'
  }
}
interface ConfirmationPendingContentProps {
  onDismiss: () => void
  pendingText?: string
}

export const ConfirmationPendingContent: React.FC<ConfirmationPendingContentProps> = ({
  onDismiss,
  pendingText
}) => {
  const confirmationPendingContent = useConfirmationPendingContent(pendingText)

  return (
    <Box padding={4} overflow='hidden'>
      <Box className='flex items-center justify-end relative mb-5'>
        <Cancel01Icon className='cursor-pointer z-10 text-white' width={24} onClick={onDismiss} />
      </Box>
      <Box className='flex justify-center my-5'>
        <Image
          className='animate-spin'
          src='https://imagedelivery.net/COrEjiX90UMaVnfrARSGdQ/1ee529ea-5721-46ac-8595-b00309599800/public'
          width={100}
          height={100}
          alt='molandak'
        />
      </Box>
      <Box className='p-2 text-center'>
        <h5 className='text-lg font-medium'>{confirmationPendingContent.title}</h5>
        {confirmationPendingContent.pending && (
          <p className='font-base'>{confirmationPendingContent.pending}</p>
        )}
        <p className='text-sm mt-2 font-medium opacity-40'>{confirmationPendingContent.confirm ?? ''}</p>
      </Box>
    </Box>
  )
}

interface TransactionSubmittedContentProps {
  onDismiss: () => void
  hash: string | undefined
  chainId: ChainId
  modalContent: string
  txPending?: boolean

}

export const TransactionSubmittedContent: React.FC<TransactionSubmittedContentProps> = ({
  onDismiss,
  chainId,
  hash,
  txPending,
  modalContent
}) => {
  return (
    <Box padding={4}>
      <Box className='flex justify-between'>
        <h5 className=''>{txPending ? 'Transaction Submitted' : 'Transaction Completed'}</h5>
        <Cancel01Icon className='cursor-pointer z-10 text-white' width={24} onClick={onDismiss} />
      </Box>
      <Box className='mt-5 py-0 text-start flex flex-col justify-center items-center gap-5'>
        <Box className='p-3 flex justify-center flex-col items-center'>
          <Box className='flex items-center justify-center gap-4'>
            <p className={`${txPending ? 'text-white' : 'text-white/50'}`}>Transaction Submitted</p>
            <CheckmarkCircle01Icon className={`${txPending ? 'text-white' : 'text-white/50'}`} />
          </Box>

          <ArrowDown02Icon className={`${txPending ? 'text-white' : 'text-white/50'} mt-5 mb-5 text-start`} />

          <Box className='flex items-center gap-4'>
            <p className={`${txPending ? 'text-white' : 'text-white/50'}`}>Pending on Blockchain</p>
            {txPending ? <CircularProgress color='secondary' className='text-primary' size='25px' /> : <CheckmarkCircle01Icon className={`${txPending ? 'text-white' : 'text-white/50'}`} />}
          </Box>
          {!txPending && <ArrowDown02Icon className={`${txPending ? 'text-white arrow-animate' : 'text-white/50'} mt-5 mb-5`} />}
          {!txPending && (
            <Box className='flex items-center gap-4'>
              <p className={`${txPending ? 'text-white' : 'text-success'}`}>Transaction Completed</p>
              <CheckmarkCircle01Icon className={`${txPending ? 'text-success' : 'text-success'}`} />
            </Box>
          )}
        </Box>
      </Box>
      <Box className='flex justify-between' mt={3}>
        {chainId && hash && (
          <a
            href={`https://testnet.monadexplorer.com/tx/${hash}`}
            target='_blank'
            rel='noopener noreferrer'
            style={{ width: '48%', textDecoration: 'none' }}
          >
            <Button className=' w-full h-12 text-md rounded-md text-textPrimary bg-primary'>View Transaction</Button>
          </a>
        )}
        <Button
          className=' w-full h-12 text-md rounded-md text-textPrimary bg-primary/70'
          style={{ width: '48%' }}
          onClick={onDismiss}
        >
          Close
        </Button>
      </Box>
    </Box>
  )
}
// }

interface ConfirmationModalContentProps {
  title: string
  onDismiss: () => void
  content: () => React.ReactNode
  id?: string
}

export const ConfirmationModalContent: React.FC<ConfirmationModalContentProps> = ({
  id,
  title,
  onDismiss,
  content
}) => {
  return (
    <Box padding={4}>
      <Box className='flex items-center justify-end relative mb-5'>
        <h5
          className='absolute w-full font-medium'
          id={id}
        >{title}
        </h5>
        <Cancel01Icon className='cursor-pointer z-10 text-white' width={20} onClick={onDismiss} />
      </Box>
      {content()}
    </Box>
  )
}

interface TransactionErrorContentProps {
  message: string
  onDismiss: () => void
}

export const TransactionErrorContent: React.FC<TransactionErrorContentProps> = ({
  message,
  onDismiss
}) => {
  return (
    <Box padding={2}>
      <Box>
        <Box className='flex items-center justify-end relative mb-5'>
          <h5 className='absolute w-full text-start font-medium'>Error</h5>
          <Cancel01Icon className='cursor-pointer text-white z-10' width={24} onClick={onDismiss} />
        </Box>
        <Box className='flex justify-center items-center flex-col mb-3 mt-3'>
          <Image src={rejected} width={200} alt='rejected' className='my-5' />
          <p className='text-lg font-clash break-all max-h-44 text-center text-white/80'>{message}</p>
        </Box>
      </Box>
      <Button
        className='w-full bg-primary py-4 px-4 rounded-md disabled:opacity-40 bg-opacity-90 text-lg mt-2'
        onClick={onDismiss}
      >
        Dismiss
      </Button>
    </Box>
  )
}

interface ConfirmationModalProps {
  isOpen: boolean
  onDismiss: () => void
  hash: string | undefined
  content: () => React.ReactNode
  attemptingTxn: boolean
  pendingText?: string
  modalContent: string
  txPending?: boolean
  modalWrapper?: string
  isTxWrapper?: boolean
}

const TransactionConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onDismiss,
  attemptingTxn,
  txPending,
  hash,
  pendingText,
  content,
  modalContent,
  modalWrapper,
  isTxWrapper = true
}) => {
  const { chainId } = useWalletData()

  if (chainId === undefined) return null

  // confirmation screen
  return (
    <CustomModal
      classname='max-w-[400px] border-none'
      open={isOpen}
      onClose={onDismiss}
      modalWrapper={`${modalWrapper ?? 'INVALID WRAPPER'}${isTxWrapper ? ' max-w-[400px]' : ''}`}
    >
      <Box position='relative' zIndex={2} className=''>
        {attemptingTxn
          ? (
            <ConfirmationPendingContent
              onDismiss={onDismiss}
              pendingText={pendingText}
            />
            )
          : hash
            ? (
              <TransactionSubmittedContent
                chainId={chainId}
                txPending={txPending}
                hash={hash}
                onDismiss={onDismiss}
                modalContent={modalContent}
              />
              )
            : (
                content()
              )}
      </Box>
    </CustomModal>
  )
}

export default TransactionConfirmationModal
